import React, { useState, useEffect, useContext, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "../../assets/css/utilisateur/utilisateurProfile.css";
import Breadcrumb from "../../components/breadcrumb/Breadcrumb";
import useUtilisateur from "../../hooks/useUtilisateur";
import defaultProfileImage from "../../assets/images/utilisateur/default.jpeg";
import LoaderProfile from "../../components/loader/utilisateur/LoaderProfile";
import { AuthContext } from "../../contexts/AuthContext";

const UtilisisateurProfile = ({ setAlert }) => {
  const { getOneUser, updateOneUser } = useUtilisateur();
  const navigate = useNavigate();
  const location = useLocation();
  const [showModal, setShowModal] = useState(false);
  const connectedUser =
    JSON.parse(localStorage.getItem("user")) ||
    JSON.parse(sessionStorage.getItem("user"));

  const isAdmin = Number(connectedUser?.role) === 1;

  const queryId = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get("id");
  }, [location.search]);

  const targetUserId = queryId || connectedUser?._id;

  // ✅ IMPORTANT : admin fields seulement si admin ET url contient ?id=
  const canSeeAdminFields = isAdmin && Boolean(queryId);

  const [bool, setBool] = useState(false);
  const [profileData, setProfileData] = useState({});

  // ✅ Admin reset password (double saisie)
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const imageBaseUrl = (
    process.env.REACT_APP_API_URL ||
    process.env.REACT_APP_ARTICLE_IMAGE ||
    ""
  ).replace(/\/+$/, "");

  const { isLoading, setIsLoading, logout } = useContext(AuthContext);

  const breadcrumbItems = useMemo(() => {
    if (canSeeAdminFields) {
      return [
        { label: "Accueil", link: "/" },
        { label: "Admin", link: "/admin" },
        { label: "Utilisateurs", link: "/admin/utilisateurs" },
        { label: "Profil" },
      ];
    }
    return [
      { label: "Accueil", link: "/" },
      { label: "Mon compte", link: "/utilisateur/compte" },
      { label: "Profil" },
    ];
  }, [canSeeAdminFields]);

  useEffect(() => {
    if (!targetUserId) return;

    setIsLoading(true);
    getOneUser(targetUserId)
      .then((res) => {
        if (res.data?.data) setProfileData(res.data.data);

        // ✅ reset champs password quand on charge un profil
        setNewPassword("");
        setConfirmPassword("");
        setShowNewPassword(false);
        setShowConfirmPassword(false);

        setIsLoading(false);
      })
      .catch((err) => {
        console.log(err);
        setIsLoading(false);
        setAlert?.({
          message:
            "Une erreur s'est produite lors de la récupération des données du profil.",
          type: "danger",
        });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bool, targetUserId]);

  const handleChange = (e) => {
    const { name, value, files, type } = e.target;

    const numericFields = [
      "role",
      "authProvider",
      "status",
      "active",
      "archive",
      "typeCompte",
    ];

    let finalValue = files ? files[0] : value;

    if (type !== "file" && numericFields.includes(name)) {
      finalValue = Number(value);
    }

    setProfileData((prev) => ({
      ...prev,
      [name]: finalValue,
    }));
  };

  const handleSubmtit = (ev) => {
    ev.preventDefault();
    ev.stopPropagation();

    if (!targetUserId) return;

    const formData = new FormData();


    const adminOnlyFields = [
      "role",
      "authProvider",
      "status",
      "active",
      "archive",
      "typeCompte",
    ];

    // 1) Champs profile (sans password)
    Object.entries(profileData).forEach(([key, value]) => {
      if (
        [
          "password",
          "passwordConfirm",
          "newPassword",
          "confirmPassword",
        ].includes(key)
      )
        return;
      if (!canSeeAdminFields && adminOnlyFields.includes(key)) return;

      if (value !== undefined && value !== null && value !== "") {
        formData.append(key, value);
      }
    });

    // 2) Reset password (uniquement admin + ?id=)
    if (canSeeAdminFields) {
      const p1 = newPassword.trim();
      const p2 = confirmPassword.trim();

      if (p1 || p2) {
        if (!p1 || !p2) {
          setAlert?.({
            message: "Veuillez saisir le mot de passe deux fois.",
            type: "danger",
          });
          return;
        }

        if (p1.length < 8) {
          setAlert?.({
            message: "Mot de passe trop court (minimum 8 caractères).",
            type: "danger",
          });
          return;
        }

        if (p1 !== p2) {
          setAlert?.({
            message: "Les deux mots de passe ne correspondent pas.",
            type: "danger",
          });
          return;
        }

        formData.append("password", p1);
        formData.append("passwordConfirm", p2);
      }
    }

    // Afficher les données envoyées (pour debug)
    for (let pair of formData.entries()) {
      console.log(pair[0] + ": " + pair[1]);
    }

    updateOneUser(`?source=profile`, targetUserId, formData)
      .then(() => {
        setAlert?.({
          message: "Information modifiée avec succès",
          type: "success",
        });

        setNewPassword("");
        setConfirmPassword("");
        setShowNewPassword(false);
        setShowConfirmPassword(false);

        if (canSeeAdminFields) {
          navigate("/admin/utilisateurs");
          return;
        }

        setBool((b) => !b);
      })
      .catch((err) => {
        console.log(err);
        setAlert?.({
          message:
            err?.response?.data?.message ||
            "Une erreur s'est produite lors de la modification des données du profil.",
          type: "danger",
        });
      });
  };

  const renderPhotoSrc = () => {
    const p = String(profileData?.photo || "").trim();
    if (!p || p === "/default.png") return defaultProfileImage;
    if (/^https?:\/\//i.test(p)) return p;
    if (p.startsWith("/")) return `${imageBaseUrl}${p}`;
    return `${imageBaseUrl}/${p}`;
  };
  const handleDeleteAccount = () => {
    updateOneUser(`?source=profile`, targetUserId, { archive: 1 })
      .then(() => {
        setAlert?.({
          message: "Votre compte a été supprimé avec succès.",
          type: "success",
        });

        logout();
      })
      .catch((err) => {
        console.log(err);
        setAlert?.({
          message:
            err?.response?.data?.message ||
            "Une erreur s'est produite lors de la suppression de votre compte.",
          type: "danger",
        });
      });
  };
  // ✅ classe “pill” pour tous les champs
  const pill = "form-control rounded-pill";

  return (
    <>
      <hr />
      <div className="container">
        <Breadcrumb items={breadcrumbItems} />

        <div className="utilisateur_profile_form">
          {isLoading ? (
            <LoaderProfile />
          ) : (
            <form onSubmit={handleSubmtit}>
              <div className="form-outline mb-4 text-center">
                <div className="utilisateur_profile_container_photo">
                  <img
                    src={renderPhotoSrc()}
                    alt="Photo Utilisateur"
                    style={{ width: "100%" }}
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = defaultProfileImage;
                    }}
                  />
                </div>

                <label className="form-label" htmlFor="form6Example4">
                  Photo de profil
                </label>

                {/* garde name="image" si ton backend attend "image" */}
                <input
                  type="file"
                  name="image"
                  onChange={handleChange}
                  className={pill}
                />
              </div>

              <div className="row g-3">
                <div className="col-12">
                  <input
                    type="text"
                    className={pill}
                    placeholder="Nom"
                    name="nom"
                    onChange={handleChange}
                    defaultValue={profileData?.nom}
                    required
                  />
                </div>

                <div className="col-12">
                  <input
                    type="text"
                    className={pill}
                    placeholder="Prénom"
                    name="prenom"
                    onChange={handleChange}
                    defaultValue={profileData?.prenom}
                    required
                  />
                </div>

                <div className="col-12">
                  <input
                    type="email"
                    className={pill}
                    placeholder="Email"
                    name="email"
                    onChange={handleChange}
                    defaultValue={profileData?.email}
                    required
                  />
                </div>

                <div className="col-12">
                  <input
                    type="tel"
                    className={pill}
                    placeholder="Téléphone"
                    name="tel"
                    onChange={handleChange}
                    defaultValue={profileData?.tel}
                  />
                </div>

                <div className="col-12">
                  <input
                    type="text"
                    className={pill}
                    placeholder="Ville"
                    name="ville"
                    onChange={handleChange}
                    defaultValue={profileData?.ville}
                  />
                </div>

                <div className="col-12">
                  <input
                    type="text"
                    className={pill}
                    placeholder="Adresse"
                    name="adresse"
                    onChange={handleChange}
                    defaultValue={profileData?.adresse}
                  />
                </div>
                <a
                  className="category_item"
                  onClick={(ev) => setShowModal(true)}
                >
                  Supprimer mon compte
                </a>

                {/* ===================== ADMIN ONLY (seulement si ?id=) ===================== */}
                {canSeeAdminFields && (
                  <>
                    <div className="col-12">
                      <label className="form-label">Type de compte</label>
                      <select
                        className={pill}
                        name="typeCompte"
                        onChange={handleChange}
                        value={Number(profileData?.typeCompte ?? 1)}
                      >
                        <option value={1}>Personnel</option>
                        <option value={2}>Professionnel</option>
                        <option value={3}>Test</option>
                      </select>
                    </div>

                    <div className="col-12">
                      <label className="form-label">Rôle</label>
                      <select
                        className={pill}
                        name="role"
                        onChange={handleChange}
                        value={Number(profileData?.role ?? 2)}
                      >
                        <option value={1}>Admin</option>
                        <option value={2}>Vendeur/Acheteur</option>
                        <option value={3}>Lecture seule</option>
                      </select>
                    </div>

                    <div className="col-12">
                      <label className="form-label">
                        Provider (authProvider)
                      </label>
                      <select
                        className={pill}
                        name="authProvider"
                        onChange={handleChange}
                        value={Number(profileData?.authProvider ?? 1)}
                      >
                        <option value={1}>Email/Password</option>
                        <option value={2}>Google</option>
                        <option value={3}>Facebook</option>
                        <option value={4}>Apple</option>
                      </select>
                    </div>

                    <div className="col-12">
                      <label className="form-label">Statut connexion</label>
                      <select
                        className={pill}
                        name="status"
                        onChange={handleChange}
                        value={Number(profileData?.status ?? 1)}
                      >
                        <option value={1}>Connecté</option>
                        <option value={2}>Déconnecté</option>
                      </select>
                    </div>

                    <div className="col-12">
                      <label className="form-label">Actif</label>
                      <select
                        className={pill}
                        name="active"
                        onChange={handleChange}
                        value={Number(profileData?.active ?? 1)}
                      >
                        <option value={1}>Actif</option>
                        <option value={2}>Désactivé</option>
                      </select>
                    </div>

                    <div className="col-12">
                      <label className="form-label">Archive</label>
                      <select
                        className={pill}
                        name="archive"
                        onChange={handleChange}
                        value={Number(profileData?.archive ?? 2)}
                      >
                        <option value={1}>Archivé</option>
                        <option value={2}>Non archivé</option>
                      </select>
                    </div>

                    {/* ===================== ADMIN: RESET PASSWORD ===================== */}
                    <div className="col-12">
                      <hr />
                      <label className="form-label" style={{ fontWeight: 600 }}>
                        Réinitialiser le mot de passe
                      </label>
                      <div
                        className="text-muted"
                        style={{ fontSize: 13, marginBottom: 10 }}
                      >
                        (Admin uniquement) Saisissez le nouveau mot de passe
                        deux fois.
                      </div>

                      <div className="mb-2" style={{ position: "relative" }}>
                        <input
                          type={showNewPassword ? "text" : "password"}
                          className={pill}
                          placeholder="Nouveau mot de passe"
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          autoComplete="new-password"
                        />
                        <button
                          type="button"
                          onClick={() => setShowNewPassword((s) => !s)}
                          style={{
                            position: "absolute",
                            right: 12,
                            top: "50%",
                            transform: "translateY(-50%)",
                            border: "none",
                            background: "transparent",
                            cursor: "pointer",
                            color: "#64748b",
                            fontWeight: 600,
                          }}
                          aria-label="Afficher/Masquer nouveau mot de passe"
                        >
                          {showNewPassword ? "Masquer" : "Afficher"}
                        </button>
                      </div>

                      <div style={{ position: "relative" }}>
                        <input
                          type={showConfirmPassword ? "text" : "password"}
                          className={pill}
                          placeholder="Confirmer le mot de passe"
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          autoComplete="new-password"
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirmPassword((s) => !s)}
                          style={{
                            position: "absolute",
                            right: 12,
                            top: "50%",
                            transform: "translateY(-50%)",
                            border: "none",
                            background: "transparent",
                            cursor: "pointer",
                            color: "#64748b",
                            fontWeight: 600,
                          }}
                          aria-label="Afficher/Masquer confirmation mot de passe"
                        >
                          {showConfirmPassword ? "Masquer" : "Afficher"}
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>

              <div className="utilisateur_profile_form_button">
                <button type="submit" className="btnRoundedPill">
                  ENREGISTRER
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
      {showModal && (
        <>
          <div class="modal" aria-modal="true" style={{ display: "block" }}>
            <div class="modal-dialog" role="document">
              <div class="modal-content">
                <div class="modal-header">
                  <h5 class="modal-title">Confirmation de suppression</h5>
                  <button
                    type="button"
                    class="btn-close"
                    data-dismiss="modal"
                    aria-label="Close"
                  ></button>
                </div>
                <div class="modal-body">
                  <p>Voulez-vous vraiment supprimer votre compte ?</p>
                </div>
                <div class="modal-footer">
                  <button
                    type="button"
                    class="btn btn-primary"
                    onClick={(ev) => {
                      ev.preventDefault();
                      ev.stopPropagation();
                      handleDeleteAccount();
                    }}
                  >
                    Valider
                  </button>
                  <button
                    type="button"
                    class="btn btn-secondary"
                    onClick={(ev) => setShowModal(false)}
                  >
                    Annuler
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default UtilisisateurProfile;
